import type { ISerialize } from '@/objects/ISerialize';
import { SignalrSubscription } from './SignalrSubscription';
import type { IBaseObjStaticConstructor } from '@/objects/IBaseObjStaticConstructor';
import { watch, type WatchHandle } from 'vue';
import { useEventStore } from '@/stores';

/**
 * Contains a list of all active subscriptions
 * the string is the subscription key
 */
const subscriptionMap: Map<string, SignalrSubscription<any>> = new Map<string, SignalrSubscription<any>>();

/**
 * Keep track of if we have set up a watcher on the selected event
 */
let watchHandleForEventChanged: WatchHandle | null = null;

/**
 * Invokes a serverside signalR method that registers a subscription
 * @remarks This should NOT be used manually, it will be used by the autogenerated signalR code. Use the autogenerated code instead
 * @param method The method to invoke on the server
 * @param subscribeParameters The parameter object to pass to the server
 * @returns An unsubscribe function that can be used to remove the subscription
 */
export const subscribeSignalr = async <T extends ISerialize>(
    type: IBaseObjStaticConstructor<T>,
    method: string,
    params: ISerialize,
    callback: (data: T[]) => void,
) => {
    const subscriptionKey = SignalrSubscription.GenerateFrontendSubscriptionKey(method, params);
    // We need to check if the subscription already exists
    let subscription = subscriptionMap.get(subscriptionKey);

    if (!subscription) {
        subscription = new SignalrSubscription<T>(
            type,
            method,
            params,
            triggerSequenceNumberRegressionAlert,
            triggerResendMissingMessagesAlert,
        );
        subscriptionMap.set(subscriptionKey, subscription);
    }

    await subscription.addCallback(callback);

    const unsubscribe = async () => {
        await unsubscribeSignalr<T>(subscription, callback);
    };

    // Set up a watcher on the eventID so we can resubscribe the subscriptions that include an eventID in the params
    if (!watchHandleForEventChanged) {
        const eventStore = useEventStore();
        watchHandleForEventChanged = watch(
            () => eventStore.eventID,
            async () => {
                // Loop all the current subscriptions
                for (const subscription of subscriptionMap.values()) {
                    // If we have an EventID
                    if ('EventID' in subscription.subscribeParameters) {
                        if (!subscription.subscribeParameters.EventID) {
                            // Skip if we have subscribed to ALL events
                            // null, undefined, 0 will be treated as ALL events
                            continue;
                        }
                        // Change the ID to the new value
                        subscription.subscribeParameters.EventID = eventStore.eventID;
                        // Resubscribe
                        await subscription.resubscribe();
                    }
                }
            },
        );
    }

    return unsubscribe;
};

/**
 * Notifies all subscriptions that we have disconnected from the server.
 * This is triggered by the signalR connection.onreconnecting event, so this is late with regards to the actual disconnect, but before we are actually connected again
 */
export const notifySubscriptionsOfDisconnect = () => {
    subscriptionMap.forEach((subscription) => {
        subscription.notifyOfDisconnect();
    });
};

/*
 * Notifies all subscriptions that we have reconnected to the server.
 * This is triggered by the signalR connection.onreconnected event, so we will try to re-subscribe to all subscriptions when we have reconnected to the server.
 */
export const notifySubscriptionsOfReconnected = async () => {
    // Then we re-subscribe to all subscriptions
    const resubscribePromises: Promise<void>[] = [];
    for (const [_, subscription] of subscriptionMap.entries()) {
        resubscribePromises.push(
            subscription.resubscribe({
                reloadPotentiallyMissingMessages: true,
            }),
        );
    }
    await Promise.all(resubscribePromises);
};

/**
 * Removes a signalr subscription.
 * This will remove the callback from the frontend, and if we dont have any other subscriptions for the method and group, it will also remove the subscription from the server.
 * @param method The backend method
 * @param subscribeParameters The parameters to the method
 * @param callback The callback that was initially registered
 */
const unsubscribeSignalr = async <T extends ISerialize>(subscription: SignalrSubscription<T>, callback: (data: T[]) => void) => {
    // We try and remove the callback from the subscription
    await subscription.removeCallback(callback);
};

/**
 * Register a callback that will trigger if we get a sequenceNumber that is lower than a previous sequenceNumber for any signalR subscription.
 *
 * The sequenceNumber should always just go up, so if this happens we know that the server has restarted, and we might have missed data
 *
 * This only triggers ONCE, since we will probably get the same regression in alot of signalR handlers, and we dont want to spam alerts
 * @param callback The callback that will be called
 */
export const registerSequenceNumberRegressionAlertCallback = (callback: () => void) => {
    if (sequenceNumberRegressionAlertCallback !== null) {
        throw new Error('This is only meant to be registered once');
    }
    sequenceNumberRegressionAlertCallback = callback;
};

let sequenceNumberRegressionAlertCallback: (() => void) | null = null;

let sequenceNumberRegressionTriggered = false;

const triggerSequenceNumberRegressionAlert = () => {
    if (sequenceNumberRegressionTriggered) {
        return;
    }
    sequenceNumberRegressionTriggered = true;

    sequenceNumberRegressionAlertCallback?.();
};

/**
 * Register a callback that will trigger if we get false success from the server when we ask it to resend missing messages
 *
 * This only triggers ONCE, since we might miss messages for several message types for one disconnection, and we dont want to spam alerts
 * @param callback The callback that will be called
 */
export const registerSignalrResendMissingMessagesAlertCallback = (callback: () => void) => {
    if (resendMissingMessagesAlertCallback !== null) {
        throw new Error('This is only meant to be registered once');
    }
    resendMissingMessagesAlertCallback = callback;
};

let resendMissingMessagesAlertCallback: (() => void) | null = null;

let resendMissingMessagesAlertTriggered = false;

const triggerResendMissingMessagesAlert = () => {
    if (resendMissingMessagesAlertTriggered) {
        return;
    }
    resendMissingMessagesAlertTriggered = true;

    resendMissingMessagesAlertCallback?.();
};
